import React, { useState } from 'react';
import { Phone, Mail, Send, CheckCircle, Upload, X } from 'lucide-react';
import { mockData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    serviceType: '',
    address: '',
    // Logu mazgāšana fields
    buildingType: '',
    buildingSize: '',
    floors: '',
    // Vitrīnu mazgāšana fields
    vitrinaSize: '',
    washingFrequency: [],
    windowAccess: '',
    // Cita veida fields
    description: '',
    // Common fields
    additionalInfo: '',
    images: []
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFrequencyChange = (value, checked) => {
    setFormData(prev => ({
      ...prev,
      washingFrequency: checked
        ? [...prev.washingFrequency, value]
        : prev.washingFrequency.filter(v => v !== value)
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files].slice(0, 5) // Max 5 images
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Validate required fields based on service type
      if (!formData.name || !formData.phone || !formData.address || !formData.serviceType) {
        throw new Error('Lūdzu, aizpildiet visus obligātos laukus (*)');
      }

      // Prepare data based on service type
      let payload = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        serviceType: formData.serviceType
      };

      // Add service-specific fields with validation
      if (formData.serviceType === 'logu_mazgasana') {
        if (!formData.buildingType || !formData.buildingSize || !formData.floors) {
          throw new Error('Lūdzu, aizpildiet visus obligātos laukus: Ēkas tips, Ēkas izmērs un Stāvi');
        }
        payload = {
          ...payload,
          buildingType: formData.buildingType,
          buildingSize: formData.buildingSize,
          floors: formData.floors,
          additionalInfo: formData.additionalInfo || null
        };
      } else if (formData.serviceType === 'vitrinu_mazgasana') {
        if (!formData.vitrinaSize || formData.washingFrequency.length === 0 || !formData.windowAccess) {
          throw new Error('Lūdzu, aizpildiet visus obligātos laukus: Vitrīnas izmērs, Mazgāšanas biežums un Piekļuve logiem');
        }
        payload = {
          ...payload,
          vitrinaSize: formData.vitrinaSize,
          washingFrequency: formData.washingFrequency,
          windowAccess: formData.windowAccess,
          additionalInfo: formData.additionalInfo || null
        };
      } else if (formData.serviceType === 'cita_veida') {
        if (!formData.description) {
          throw new Error('Lūdzu, aprakstiet jūsu stiklus');
        }
        payload = {
          ...payload,
          description: formData.description
        };
      }

      // Create FormData to send files
      const formDataToSend = new FormData();
      formDataToSend.append('data', JSON.stringify(payload));
      
      // Add image files
      formData.images.forEach((file) => {
        formDataToSend.append('images', file);
      });

      // Call backend API
      const response = await fetch('/api/bookings/', {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Kļūda nosūtot pieteikumu';
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            const fieldErrors = errorData.detail.map(err => {
              const field = err.loc ? err.loc[err.loc.length - 1] : 'nezināms lauks';
              return `${field}: ${err.msg}`;
            }).join(', ');
            errorMessage = `Kļūda validācijā: ${fieldErrors}`;
          } else {
            errorMessage = errorData.detail;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Booking created:', data);
      
      setSubmitted(true);
      setError(null);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          phone: '',
          serviceType: '',
          address: '',
          buildingType: '',
          buildingSize: '',
          floors: '',
          vitrinaSize: '',
          washingFrequency: [],
          windowAccess: '',
          description: '',
          additionalInfo: '',
          images: []
        });
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.message || 'Kļūda nosūtot pieteikumu. Lūdzu, mēģiniet vēlreiz vai zvaniet mums.');
    } finally {
      setLoading(false);
    }
  };

  const renderDynamicFields = () => {
    if (formData.serviceType === 'logu_mazgasana') {
      return (
        <>
          {/* Ēkas tips */}
          <div className="space-y-2">
            <Label htmlFor="buildingType">Ēkas tips *</Label>
            <Select
              value={formData.buildingType}
              onValueChange={(value) => handleSelectChange('buildingType', value)}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Izvēlieties ēkas tipu" />
              </SelectTrigger>
              <SelectContent>
                {mockData.buildingTypes.map((building) => (
                  <SelectItem key={building.value} value={building.value}>
                    {building.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Ēkas izmērs */}
          <div className="space-y-3">
            <Label>Ēkas izmērs *</Label>
            <RadioGroup value={formData.buildingSize} onValueChange={(value) => handleSelectChange('buildingSize', value)} required>
              {mockData.buildingSizes.map((size) => (
                <div key={size.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={size.value} id={size.value} />
                  <Label htmlFor={size.value} className="font-normal cursor-pointer">{size.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Stāvi */}
          <div className="space-y-3">
            <Label>Stāvi *</Label>
            <RadioGroup value={formData.floors} onValueChange={(value) => handleSelectChange('floors', value)} required>
              {(formData.buildingType === 'dzivoklis' ? mockData.floorsApartment : mockData.floors).map((floor) => (
                <div key={floor.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={floor.value} id={floor.value} />
                  <Label htmlFor={floor.value} className="font-normal cursor-pointer">{floor.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Papildu informācija */}
          <div className="space-y-2">
            <Label htmlFor="additionalInfo">Papildu informācija</Label>
            <Textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              placeholder="Vai ir vēl kaut kas, ko mums būtu jāzin par Jūsu logiem?"
              rows={3}
            />
          </div>

          {/* Image upload */}
          <div className="space-y-2">
            <Label htmlFor="images">Ielādējiet bildes (nav obligāti)</Label>
            <p className="text-sm text-gray-600">Ēkas bildes no visām pusēm ļauj mums iedot Jums precīzu cenu</p>
            <Input
              id="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            {formData.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.images.map((file, index) => (
                  <div key={index} className="relative">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-500">{file.name.substring(0, 10)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    if (formData.serviceType === 'vitrinu_mazgasana') {
      return (
        <>
          {/* Vitrīnas stiklu daudzums */}
          <div className="space-y-3">
            <Label>Vitrīnas stiklu daudzums *</Label>
            <RadioGroup value={formData.vitrinaSize} onValueChange={(value) => handleSelectChange('vitrinaSize', value)} required>
              {mockData.vitrinaSizes.map((size) => (
                <div key={size.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={size.value} id={`vitrina-${size.value}`} />
                  <Label htmlFor={`vitrina-${size.value}`} className="font-normal cursor-pointer">{size.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Mazgāšanas biežums */}
          <div className="space-y-3">
            <Label>Mazgāšanas biežums * (var izvēlēties vairākus)</Label>
            <div className="space-y-2">
              {mockData.washingFrequency.map((freq) => (
                <div key={freq.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={freq.value}
                    checked={formData.washingFrequency.includes(freq.value)}
                    onCheckedChange={(checked) => handleFrequencyChange(freq.value, checked)}
                  />
                  <Label htmlFor={freq.value} className="font-normal cursor-pointer">{freq.label}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Piekļuve logiem */}
          <div className="space-y-3">
            <Label>Piekļuve logiem *</Label>
            <RadioGroup value={formData.windowAccess} onValueChange={(value) => handleSelectChange('windowAccess', value)} required>
              {mockData.windowAccess.map((access) => (
                <div key={access.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={access.value} id={`access-${access.value}`} />
                  <Label htmlFor={`access-${access.value}`} className="font-normal cursor-pointer">{access.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Papildu informācija */}
          <div className="space-y-2">
            <Label htmlFor="additionalInfo">Papildu informācija</Label>
            <Textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              placeholder="Vai ir vēl kaut kas, ko mums būtu jāzin par Jūsu vitrīnu?"
              rows={3}
            />
          </div>

          {/* Image upload */}
          <div className="space-y-2">
            <Label htmlFor="images">Ielādējiet bildes (nav obligāti)</Label>
            <p className="text-sm text-gray-600">Vitrīnas bildes no visām pusēm ļauj mums iedot Jums precīzu cenu</p>
            <Input
              id="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            {formData.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.images.map((file, index) => (
                  <div key={index} className="relative">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-500">{file.name.substring(0, 10)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    if (formData.serviceType === 'cita_veida') {
      return (
        <>
          {/* Aprakstiet stiklus */}
          <div className="space-y-2">
            <Label htmlFor="description">Aprakstiet Jūsu stiklus *</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Kāda veida stiklu Jūs vēlaties iztīrīt?"
              rows={4}
              required
            />
          </div>

          {/* Image upload */}
          <div className="space-y-2">
            <Label htmlFor="images">Ielādējiet bildes (nav obligāti)</Label>
            <p className="text-sm text-gray-600">Ēkas/stiklu bildes no visām pusēm ļauj mums iedot Jums precīzu cenu</p>
            <Input
              id="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            {formData.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.images.map((file, index) => (
                  <div key={index} className="relative">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-500">{file.name.substring(0, 10)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <section id="kontakti" className="py-8 sm:py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 md:mb-16">
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 md:px-4 md:py-2 bg-sky-100 text-sky-700 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
            <Phone className="w-3 h-3 md:w-4 md:h-4" />
            <span>Sazinies ar mums</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-4 px-2">
            Pieteikt logu tīrīšanu
          </h2>
          <p className="text-sm sm:text-base md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Aizpildiet formu, un mēs ar jums sazināsimies 3 stundu laikā
          </p>
        </div>

        {/* Mobile Contact Cards - Show on top on mobile */}
        <div className="lg:hidden space-y-3 mb-6">
          <a href={`tel:${mockData.company.phone}`} className="block">
            <Card className="border border-sky-100 hover:shadow-md transition-shadow">
              <CardContent className="p-3 flex items-center space-x-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-sky-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm">Telefons</p>
                  <p className="text-sky-500 font-medium text-sm">{mockData.company.phone}</p>
                  <p className="text-xs text-gray-600">{mockData.company.workingHours}</p>
                </div>
              </CardContent>
            </Card>
          </a>

          <a href={`mailto:${mockData.company.email}`} className="block">
            <Card className="border border-sky-100 hover:shadow-md transition-shadow">
              <CardContent className="p-3 flex items-center space-x-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-sky-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm">E-pasts</p>
                  <p className="text-sky-500 font-medium text-xs break-all">{mockData.company.email}</p>
                  <p className="text-xs text-gray-600">{mockData.company.responseTime}</p>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Desktop Contact Info Cards */}
          <div className="hidden lg:block space-y-6 lg:col-span-1">
            <Card className="border-2 border-sky-100 hover:shadow-lg transition-shadow">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Telefons</h3>
                    <a 
                      href={`tel:${mockData.company.phone}`}
                      className="text-sky-500 hover:text-sky-600 font-medium"
                    >
                      {mockData.company.phone}
                    </a>
                    <p className="text-sm text-gray-600 mt-1">{mockData.company.workingHours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-sky-100 hover:shadow-lg transition-shadow">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-sky-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">E-pasts</h3>
                    <a 
                      href={`mailto:${mockData.company.email}`}
                      className="text-sky-500 hover:text-sky-600 font-medium break-all text-xs sm:text-sm"
                    >
                      {mockData.company.email}
                    </a>
                    <p className="text-sm text-gray-600 mt-1">{mockData.company.responseTime}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Image */}
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_window-care/artifacts/pllsa3fr_image.png"
                alt="Window cleaning tools"
                className="w-full h-48 lg:h-64 object-cover"
              />
            </div>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="border border-gray-200 md:border-2 md:border-gray-100 shadow-md md:shadow-lg">
              <CardContent className="p-4 sm:p-4 md:p-6 lg:p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Paldies!</h3>
                    <p className="text-gray-600">
                      Jūsu pieteikums ir saņemts. Mēs ar jums sazināsimies drīzumā!
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    {/* Name and Phone */}
                    <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="name" className="text-sm">Jūsu vārds *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Jūsu vārds"
                          required
                          className="w-full h-10"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="phone" className="text-sm">Telefons *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+371 20000000"
                          required
                          className="w-full h-10"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-1.5">
                      <Label htmlFor="address" className="text-sm">Adrese *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Brīvības iela 1, Rīga"
                        required
                        className="w-full h-10"
                      />
                    </div>

                    {/* Service Type */}
                    <div className="space-y-1.5">
                      <Label htmlFor="serviceType" className="text-sm">Pakalpojuma veids *</Label>
                      <Select
                        value={formData.serviceType}
                        onValueChange={(value) => handleSelectChange('serviceType', value)}
                        required
                      >
                        <SelectTrigger className="w-full h-10">
                          <SelectValue placeholder="Izvēlieties pakalpojumu" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockData.serviceTypes.map((service) => (
                            <SelectItem key={service.value} value={service.value}>
                              {service.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dynamic Fields Based on Service Type */}
                    {renderDynamicFields()}

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                        <p className="text-red-700 text-sm font-medium">❌ {error}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm sm:text-base md:text-lg h-auto min-h-[44px] md:h-12 py-3 px-4 whitespace-normal leading-tight"
                    >
                      {loading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          <span>Nosūta...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                          <span className="flex-1 text-center">Saņemt aptuvenu cenas piedāvājumu</span>
                        </>
                      )}
                    </Button>

                    <p className="text-xs md:text-sm text-gray-500 text-center px-2">
                      Nosūtot formu, jūs piekrītat personas datu apstrādei
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
